// deps
import { useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";

// internal-deps
import { useMountedRef } from "@swiggy-private/core-ui";
import { ConfirmPennyTransactionResponse, OrderApiData } from "@swiggy-private/payment-types";

// hooks
import usePolling, { PollingFnData } from "@payment-client/hooks/UsePolling";
import {
    useNavigate,
    CheckoutRoute,
    CheckoutContext,
    ApiContext,
    CheckoutActionTypes,
    getBridgeSdk,
    useOnBack,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    PublicApiContext,
} from "@swiggy-private/checkout-core";

// helpers
import { cleanProcessTransactionState } from "@payment-client/helpers/CheckoutStateHelper";
import { PollingEnabledPaymentFlow } from "@payment-client/includes/PaymentFlow";
import { getSecureCardData, handleConfirmTransactionSuccess } from "@payment-client/helpers/TransactionHelper";
import { isIos } from "@payment-client/helpers/Misc";

// interfaces
import { ApiResponse } from "@payment-client/interfaces/Api";

// apis
import { confirmPennyTransactionPublic } from "@payment-client/apis/publicPayment";
import { confirmPennyTransaction } from "@payment-client/apis/Payment";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

interface ConfirmOrderProps {
    pollingIntervalInMs: number;
    pollingDurationInMs: number;
    isWebPollingEnabled?: boolean;
}

type useCofirmTransactionReturnType = [boolean];

const MOVE_BACK_TWO_STATES = 2;
const MOVE_BACK_ONE_STATE = 1;

const useCofirmTransaction = ({ isWebPollingEnabled, ...props }: ConfirmOrderProps): useCofirmTransactionReturnType => {
    const isMountedRef = useMountedRef();
    const pollingRef = useRef(false);
    const confirmOrderRetryCountRef = useRef(0);
    const apiContextValue = useContext(ApiContext);
    const publicApiConfig = useContext(PublicApiContext);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const [isOrderConfirmed, setOrderConfirmed] = useState(false);

    const navigateTo = useNavigate();
    const goBack = useOnBack();

    const isPublicApiEnabledForConfirmTransaction = useMemo(() => {
        return isPublicApiEnabled(
            publicApiConfig || {},
            CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.PAYMENT_APIS,
            CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.CONFIRM_PENNY_TRANSACTION,
        );
    }, [publicApiConfig]);

    // This is used to store error response of confirm order api while polling
    // It always contains the error response of last api polled
    const confirmTransactionApiErr = useRef<ApiResponse>();
    const shouldRetryOnFailure = useCallback(
        (data?: OrderApiData) => confirmOrderRetryCountRef.current < 3 && data != null && data.retryCount > 0,
        [confirmOrderRetryCountRef],
    );

    const handleConfirmTransactionDataCallback = useCallback(
        (data: ApiResponse<ConfirmPennyTransactionResponse>) => {
            if (checkoutState.type !== CheckoutActionTypes.CONFIRM_TRANSACTION || !isMountedRef.current) return;

            const transaction = handleConfirmTransactionSuccess(data);

            setCheckoutState({
                type: CheckoutActionTypes.CONFIRM_TRANSACTION,
                payload: {
                    orderId: checkoutState.payload.orderId,
                    transaction,
                    transactionReqData: checkoutState.payload.transactionReqData,
                    useJuspaySDK: checkoutState.payload.useJuspaySDK,
                },
            });

            setOrderConfirmed(true);
        },
        [checkoutState.type, checkoutState.payload, isMountedRef, setCheckoutState],
    );

    const confirmTransaction = isPublicApiEnabledForConfirmTransaction
        ? confirmPennyTransactionPublic
        : confirmPennyTransaction;

    // confirm order api function to be passed to polling function
    const confirmTransactionApi = useCallback(async (): Promise<void> => {
        if (!isMountedRef.current || checkoutState.type !== CheckoutActionTypes.CONFIRM_TRANSACTION) {
            return Promise.resolve();
        }

        try {
            const handleConfirmTransactionData = await confirmTransaction(apiContextValue.host.unwrap(), {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                order_id: checkoutState.payload.orderId,
            });
            handleConfirmTransactionDataCallback(handleConfirmTransactionData);

            // stop polling after confirm order success
            return Promise.resolve();
        } catch (err) {
            // continue polling if we retry count > 0
            if (err != null && shouldRetryOnFailure(err.data)) {
                confirmOrderRetryCountRef.current += 1;
                return Promise.reject();
            }

            // storing err response to handle it after polling is completed
            confirmTransactionApiErr.current = err;

            // stop polling on failure
            return Promise.resolve();
        }
    }, [
        isMountedRef,
        checkoutState.type,
        checkoutState.payload,
        apiContextValue.host,
        handleConfirmTransactionDataCallback,
        shouldRetryOnFailure,
        confirmTransaction,
    ]);

    // error handling after polling is completed
    const postPollingHandlerFn = useCallback((): void => {
        if (
            !isMountedRef.current ||
            !confirmTransactionApiErr.current ||
            checkoutState.type !== CheckoutActionTypes.CONFIRM_TRANSACTION
        ) {
            return;
        }

        // TODO: add logic for fallback
        const { transaction, transactionReqData, useJuspaySDK } = checkoutState.payload;

        //Event for Transaction failed
        getBridgeSdk()
            .transactionFailedEvent({})
            .catch(() => void 0);
        cleanProcessTransactionState();

        // update checkout state to handle payment fallback status
        setCheckoutState({
            type: CheckoutActionTypes.TRANSACTION_FAILED,
            payload: {
                transaction,
                transactionReqData,
                useJuspaySDK,
            },
        });

        const secureCardData = getSecureCardData(checkoutState.payload.transactionReqData);
        useJuspaySDK
            ? goBack(isWebPollingEnabled ? MOVE_BACK_TWO_STATES : MOVE_BACK_ONE_STATE) // If Polling is enabled, the for juspay sdk state, we need to go back 3 states to move to secure card route
            : navigateTo(CheckoutRoute.SECURE_CARD, secureCardData, true);
    }, [
        checkoutState.payload,
        checkoutState.type,
        goBack,
        isMountedRef,
        navigateTo,
        setCheckoutState,
        isWebPollingEnabled,
    ]);

    const pollingFn = usePolling(postPollingHandlerFn);
    const invokePollFn = useCallback(
        (pollingData: PollingFnData) => {
            pollingFn(pollingData);
        },
        [pollingFn],
    );

    useEffect(() => {
        // is mounted check..
        if (
            pollingRef.current ||
            !isMountedRef.current ||
            checkoutState.type !== CheckoutActionTypes.CONFIRM_TRANSACTION
        ) {
            return;
        }

        // Set App Idle state to default on coming to confirm order
        if (isIos()) {
            getBridgeSdk()
                .setAppStateInIdleMode({ active: false })
                .catch(() => void 0);
        }

        pollingRef.current = true;

        let _promise = Promise.resolve(false);
        const _paymentFlowInstance = PollingEnabledPaymentFlow.getInstance();
        const _orderId = checkoutState.payload.orderId;

        if (_paymentFlowInstance && _paymentFlowInstance.getOrderId() == _orderId) {
            const _confirmTransactionPromise = _paymentFlowInstance.getConfirmPennyTransactionPromise();
            if (_confirmTransactionPromise == null) {
                _paymentFlowInstance.stop();
            } else {
                _promise = _confirmTransactionPromise
                    .then((response) => {
                        try {
                            handleConfirmTransactionDataCallback(response);
                            return true;
                        } catch (err) {
                            return false;
                        }
                    })
                    .catch((_) => false)
                    .then((r) => {
                        _paymentFlowInstance?.stop();
                        return r;
                    });
            }
        }

        // Initiate poll for confirm order
        _promise.then((result) => {
            if (result || isOrderConfirmed || !isMountedRef.current) {
                return;
            }

            invokePollFn({
                pollingDurationInMs: props.pollingDurationInMs,
                pollingIntervalInMs: props.pollingIntervalInMs,
                apiFn: confirmTransactionApi,
            });
        });
    }, [
        pollingRef,
        checkoutState,
        isMountedRef,
        setOrderConfirmed,
        isOrderConfirmed,
        props.pollingDurationInMs,
        props.pollingIntervalInMs,
        confirmTransactionApi,
        invokePollFn,
        handleConfirmTransactionDataCallback,
    ]);

    return [isOrderConfirmed];
};

export default useCofirmTransaction;
